import React, { useState } from 'react';
import { Screen } from '../../types/screen';
import { Card, Dropdown, Button } from '../UI';

interface ScreenViewProps {
  screen: Screen;
  onUpdateScreen: (screenId: string, updates: Partial<Screen>) => void;
  isSaving?: boolean;
}

const SYMBOLS = [
  { value: 'AAPL', label: 'AAPL - Apple Inc.' },
  { value: 'GOOGL', label: 'GOOGL - Alphabet Inc.' },
  { value: 'MSFT', label: 'MSFT - Microsoft Corporation' },
  { value: 'AMZN', label: 'AMZN - Amazon.com Inc.' },
  { value: 'TSLA', label: 'TSLA - Tesla Inc.' },
  { value: 'META', label: 'META - Meta Platforms Inc.' },
  { value: 'NVDA', label: 'NVDA - NVIDIA Corporation' },
  { value: 'SPY', label: 'SPY - SPDR S&P 500 ETF' },
];

const TIMEFRAMES = [
  { value: '1m', label: '1 Minute' },
  { value: '5m', label: '5 Minutes' },
  { value: '15m', label: '15 Minutes' },
  { value: '30m', label: '30 Minutes' },
  { value: '1h', label: '1 Hour' },
  { value: '4h', label: '4 Hours' },
  { value: '1d', label: '1 Day' },
  { value: '1w', label: '1 Week' },
];

const ScreenView: React.FC<ScreenViewProps> = ({
  screen,
  onUpdateScreen,
  isSaving = false,
}) => {
  const [localSymbol, setLocalSymbol] = useState(screen.symbol);
  const [localTimeframe, setLocalTimeframe] = useState(screen.timeframe);
  
  const handleSymbolChange = (value: string | number) => {
    const newSymbol = value as string;
    setLocalSymbol(newSymbol);
    onUpdateScreen(screen.id, { symbol: newSymbol });
  };
  
  const handleTimeframeChange = (value: string | number) => {
    const newTimeframe = value as string;
    setLocalTimeframe(newTimeframe);
    onUpdateScreen(screen.id, { timeframe: newTimeframe });
  };
  
  return (
    <div className="h-full flex flex-col bg-gray-50">
      <div className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{screen.name}</h1>
            <p className="text-sm text-gray-500 mt-1">
              Last updated: {new Date(screen.updatedAt).toLocaleString()}
            </p>
          </div>
          {isSaving && (
            <div className="flex items-center text-sm text-gray-500">
              <svg className="animate-spin h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Saving...
            </div>
          )}
        </div>
        
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex-1 min-w-[200px]">
            <Dropdown
              label="Symbol"
              options={SYMBOLS}
              value={localSymbol}
              onChange={handleSymbolChange}
              size="sm"
            />
          </div>
          <div className="flex-1 min-w-[200px]">
            <Dropdown
              label="Timeframe"
              options={TIMEFRAMES}
              value={localTimeframe}
              onChange={handleTimeframeChange}
              size="sm"
            />
          </div>
        </div>
      </div>
      
      <div className="flex-1 overflow-auto p-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2">
            <Card className="h-[600px] flex items-center justify-center bg-white">
              <div className="text-center">
                <svg className="mx-auto h-16 w-16 text-gray-400 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
                <h3 className="text-lg font-medium text-gray-900 mb-2">Chart View</h3>
                <p className="text-sm text-gray-500">
                  Chart visualization for {screen.symbol} ({screen.timeframe})
                </p>
                <p className="text-xs text-gray-400 mt-2">
                  Integrate your charting library here (e.g., TradingView, Chart.js, etc.)
                </p>
              </div>
            </Card>
          </div>
          
          <div className="space-y-6">
            <Card>
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Indicators</h2>
              {screen.indicators.length === 0 ? (
                <div className="text-center py-8">
                  <p className="text-sm text-gray-500 mb-4">No indicators added</p>
                  <Button variant="outline" size="sm">
                    Add Indicator
                  </Button>
                </div>
              ) : (
                <div className="space-y-3">
                  {screen.indicators.map((indicator) => (
                    <div
                      key={indicator.id}
                      className="p-3 bg-gray-50 rounded-lg border border-gray-200"
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-medium text-gray-900">{indicator.name}</p>
                          <p className="text-xs text-gray-500 mt-0.5">{indicator.type}</p>
                        </div>
                        <button className="text-gray-400 hover:text-red-600">
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </div>
                    </div>
                  ))}
                  <Button variant="outline" size="sm" fullWidth>
                    Add Indicator
                  </Button>
                </div>
              )}
            </Card>
            
            <Card>
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Screen Info</h2>
              <dl className="space-y-3 text-sm">
                <div>
                  <dt className="text-gray-500">Created</dt>
                  <dd className="text-gray-900 font-medium mt-0.5">
                    {new Date(screen.createdAt).toLocaleDateString()}
                  </dd>
                </div>
                <div>
                  <dt className="text-gray-500">Last Updated</dt>
                  <dd className="text-gray-900 font-medium mt-0.5">
                    {new Date(screen.updatedAt).toLocaleDateString()}
                  </dd>
                </div>
                <div>
                  <dt className="text-gray-500">Indicators Count</dt>
                  <dd className="text-gray-900 font-medium mt-0.5">
                    {screen.indicators.length}
                  </dd>
                </div>
              </dl>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ScreenView;

